import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationError, Router, RouterModule } from '@angular/router';
import {
  Subject,
  takeUntil,
  distinctUntilChanged,
  pairwise,
  filter,
} from 'rxjs';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { DeviceService, InitLanguage } from '@beautyapp/core';
import { SwUpdate } from '@angular/service-worker';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { AuthState, Logout } from '@beautyapp/auth';
import { shouldReloadForUnrecoverable } from './sw-recovery';
import { isChunkLoadError, shouldReloadForChunkError } from './chunk-recovery';

@Component({
  imports: [RouterModule, NzModalModule],
  selector: 'beautyapp-root',
  template: ` <router-outlet></router-outlet>`,
})
export class AppComponent implements OnInit, OnDestroy {
  private actions = inject(Actions);
  private store = inject(Store);
  private router = inject(Router);
  private permissionsService = inject(NgxPermissionsService);

  deviceService = inject(DeviceService);
  swUpdate = inject(SwUpdate);
  message = inject(NzMessageService);
  modal = inject(NzModalService);
  sub$: Subject<any> = new Subject<any>();

  ngOnInit(): void {
    this.actions
      .pipe(takeUntil(this.sub$), ofActionDispatched(Logout))
      .subscribe(() => this.router.navigate(['/auth/login']));

    // Session-loss guard. The route AuthGuard only runs on navigation, so if the
    // token expires (or the auth state is cleared) WHILE the user is sitting on a
    // page, nothing redirects them — the page stays mounted and keeps firing
    // requests that the backend rejects with 403 "Branch not provided" (the
    // global TenantGuard runs before the JWT guard). Watch isAuthenticated and,
    // on a genuine true -> false transition, bounce to login. pairwise() means a
    // cold start (a single `false`) never triggers a redirect, so public pages
    // and the login screen itself are unaffected.
    this.store
      .select(AuthState.isAuthenticated)
      .pipe(
        takeUntil(this.sub$),
        distinctUntilChanged(),
        pairwise(),
        filter(
          ([wasAuthenticated, isAuthenticated]) =>
            wasAuthenticated && !isAuthenticated
        )
      )
      .subscribe(() => this.router.navigate(['/auth/login']));

    // Subscribe to language changes
    this.store
      .select((state) => state.i18n.currentLang)
      .pipe(takeUntil(this.sub$))
      .subscribe((lang) => {
        if (lang) {
          this.store.dispatch(new InitLanguage(lang));
        }
      });

    this.store
      .select(AuthState.getRoles)
      .pipe(takeUntil(this.sub$))
      .subscribe((role: any) => {
        this.permissionsService.loadPermissions(role);
      });

    // Root level on purpose: the portal also listens, but only after login, and
    // an unrecoverable service worker can keep the app from ever getting there
    // (the v5.66.5 white screen). Reload once; the guard prevents a loop.
    if (this.swUpdate.isEnabled) {
      this.swUpdate.unrecoverable.pipe(takeUntil(this.sub$)).subscribe((event) => {
        console.error('Service worker unrecoverable state:', event.reason);
        let storage: Storage | null = null;
        try {
          storage = window.sessionStorage;
        } catch {
          storage = null;
        }
        if (shouldReloadForUnrecoverable(storage, Date.now())) {
          window.location.reload();
        }
      });
    }

    // A tab left open across a deploy still runs the old build; its lazy routes
    // point at chunk names the new build no longer serves. Load the target URL
    // in full once: index.html is no-cache, so that brings the new build. The
    // guard stops a loop if a chunk is missing for good.
    this.router.events
      .pipe(
        takeUntil(this.sub$),
        filter((e): e is NavigationError => e instanceof NavigationError)
      )
      .subscribe((event) => {
        if (!isChunkLoadError(event.error)) return;
        let storage: Storage | null = null;
        try {
          storage = window.sessionStorage;
        } catch {
          storage = null;
        }
        if (shouldReloadForChunkError(storage, Date.now())) {
          window.location.assign(event.url);
        }
      });

    const deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      this.deviceService.getDeviceId().then((id) => {
        localStorage.setItem('deviceId', id);
      });
    }
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }
}
